import { Box } from "@chakra-ui/react";
import { ColorIcon, customEvent, useResponsive } from "goi_common";
import { baseColors, BaseButton, BaseStack } from "goi_common/src/design-system";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { HEADER_HEIGHT, SECOND_HEADER_HEIGHT } from "@/constants/components";
import { useGoiCall } from "@/hooks/useGoiCall";
import { GOI_PHONE, SANGJO_LANDING_EVENT_PATH, SangjoLandingTrigger } from "./sangjoLandingData";
import SangjoLandingHero from "./SangjoLandingHero";
import SangjoLandingGuide from "./SangjoLandingGuide";
import SangjoLandingDiff from "./SangjoLandingDiff";
import SangjoLandingTrust from "./SangjoLandingTrust";
import SangjoLandingForm, { SangjoLandingFormValue } from "./SangjoLandingForm";
import SangjoLandingConsultSheet from "./SangjoLandingConsultSheet";
import SangjoLandingExitModal from "./SangjoLandingExitModal";
import { useSangjoLandingLeaveGuard } from "./useSangjoLandingLeaveGuard";

/**
 * 상조 랜딩. /match/request?template=sangjo 에서 렌더링한다.
 * 헤더·상단 네비는 플랫폼 공용, 하단은 MatchRequestContainer 의 RenewalFooter.
 * 하단 fixed CTA 는 폼에 도달하면 숨긴다. 뒤로가기 시 100원 상조 이탈방지 모달(세션당 1회).
 */
export default function SangjoLandingContainer() {
  const router = useRouter();
  const { isBiggerThanLg } = useResponsive();
  const { goiCall } = useGoiCall({
    showModal: false,
    phone: GOI_PHONE,
    path: SANGJO_LANDING_EVENT_PATH,
  });
  const { isOpen: isExitModalOpen, leave } = useSangjoLandingLeaveGuard();

  const formRef = useRef<HTMLDivElement>(null);
  const [formReached, setFormReached] = useState(false);
  const [sheetValue, setSheetValue] = useState<SangjoLandingFormValue | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // PC 56px, 모바일 102px(로고 헤더 + 상단 네비). 공용 헤더가 fixed 라 폼 스크롤 시 가려지지 않게 뺀다
  const headerOffset = isBiggerThanLg ? HEADER_HEIGHT : HEADER_HEIGHT + SECOND_HEADER_HEIGHT;

  // 폼이 보이거나 이미 지나쳤으면(아래 footer) 하단 fixed CTA 를 숨긴다
  useEffect(() => {
    const el = formRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setFormReached(entry.isIntersecting || entry.boundingClientRect.top < 0),
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // 랜딩 노출 1회. ?template=sangjo 진입은 Hackle 노출이 없어 모수 집계를 이 이벤트로 한다
  useEffect(() => {
    customEvent({
      newGtm: { path: SANGJO_LANDING_EVENT_PATH, trigger_and_target: "landing_view" },
    });
  }, []);

  // 스크롤 뎁스 25/50/75/100% 각 1회
  useEffect(() => {
    const fired = new Set<number>();
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      if (total <= 0) return;
      const pct = (window.scrollY / total) * 100;
      ([25, 50, 75, 100] as const).forEach((m) => {
        if (pct >= m && !fired.has(m)) {
          fired.add(m);
          customEvent({
            newGtm: { path: SANGJO_LANDING_EVENT_PATH, trigger_and_target: `scroll_${m}` },
          });
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const fireEvent = (trigger_and_target: SangjoLandingTrigger) =>
    customEvent({ newGtm: { path: SANGJO_LANDING_EVENT_PATH, trigger_and_target } });

  const goForm = () => {
    const el = formRef.current;
    if (!el) return;
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - headerOffset,
      behavior: "smooth",
    });
  };

  const goMatchRequest = () => router.push("/match/request");

  return (
    <BaseStack width="100%" style={{ wordBreak: "keep-all" }}>
      <SangjoLandingHero
        onRequest={() => {
          fireEvent("hero_btn_request");
          goMatchRequest();
        }}
        onCall={() => {
          fireEvent("hero_btn_call");
          goiCall();
        }}
      />
      <SangjoLandingGuide />
      <SangjoLandingDiff
        onGoForm={() => {
          fireEvent("diff_btn_form");
          goForm();
        }}
        onRequest={() => {
          fireEvent("diff_btn_request");
          goMatchRequest();
        }}
      />
      <SangjoLandingTrust />
      <SangjoLandingForm
        ref={formRef}
        submitted={submitted}
        onCall={goiCall}
        onOpenConsult={setSheetValue}
      />

      {/* 하단 fixed CTA — 폼으로 스크롤 / 전화 상담. 폼 도달 시 숨김 */}
      <Box
        position="fixed"
        bottom="0"
        left="50%"
        width="100%"
        maxWidth="480px"
        bg={baseColors.background_normal}
        borderTop={`1px solid ${baseColors.line_normal}`}
        p="12px 20px calc(12px + env(safe-area-inset-bottom))"
        zIndex={100}
        style={{
          opacity: formReached ? 0 : 1,
          transform: formReached ? "translate(-50%, 100%)" : "translate(-50%, 0)",
          transition:
            "opacity 200ms cubic-bezier(0.22,1,0.36,1), transform 200ms cubic-bezier(0.22,1,0.36,1)",
          pointerEvents: formReached ? "none" : "auto",
        }}
      >
        <BaseStack direction="horizontal" spacing={8}>
          <Box flexShrink={0}>
            <BaseButton
              type="assistive_1"
              outlined
              size="large"
              onClick={() => {
                fireEvent("fixed_call");
                goiCall();
              }}
            >
              <BaseStack direction="horizontal" spacing={4} alignItems="center" px="4px">
                <ColorIcon name="icon-call-mono" size={20} color={baseColors.label_alternative_1} />
                <Box className="body_1_normal_m">전화 상담</Box>
              </BaseStack>
            </BaseButton>
          </Box>
          <Box flex={1}>
            <BaseButton
              type="primary"
              size="large"
              onClick={() => {
                fireEvent("fixed_cta");
                goForm();
              }}
            >
              <Box className="body_1_normal_m">무료로 예상 비용 받아보기</Box>
            </BaseButton>
          </Box>
        </BaseStack>
      </Box>

      <SangjoLandingConsultSheet
        isOpen={sheetValue !== null}
        formValue={sheetValue ?? { regionText: "", condolers: "", status: "" }}
        onClose={() => setSheetValue(null)}
        onSubmitted={() => {
          setSheetValue(null);
          setSubmitted(true);
        }}
      />

      <SangjoLandingExitModal isOpen={isExitModalOpen} onLeave={leave} />
    </BaseStack>
  );
}
